import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DesignCrossesContext } from './design-crosses.context';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { CrossService } from '../shared/study/service/cross.service';
import { AlertService } from '../shared/alert/alert.service';
import { NgbActiveModal, NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ParamContext } from '../shared/service/param.context';
import { HelpService } from '../shared/service/help.service';
import { BreedingMethodFilterTypeEnum } from '../shared/breeding-methods-select/breeding-methods-select.component';
import { CrossType, PreviewCrossesModel } from '../shared/study/model/preview-crosses.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { PreviewCrossesRequestModel } from '../shared/study/model/preview-crosses.request.model';
import { ExistingCrossesModalComponent } from './existing-crosses.modal.component';
import { GermplasmListCreationComponent } from '../shared/list-creation/germplasm-list-creation.component';
import { GermplasmListEntry } from '../shared/list-creation/model/germplasm-list';
import { CrossNamePresetSetting, CrossNameSetting, NamingSource, SaveCrossesRequestModel } from '../shared/study/model/save-crosses.request.model';
import { DateHelperService } from '../shared/service/date.helper.service';
import { GermplasmService } from '../shared/germplasm/service/germplasm.service';
import { GermplasmNameSettingModel } from '../shared/germplasm/model/germplasm-name-setting.model';
import { SaveTemplateComponent } from '../study-manager/advance/save-template.component';
import { ModalConfirmComponent } from '../shared/modal/modal-confirm.component';
import { UrlService } from '../shared/service/url.service';
import { GermplasmDetailsPopupService } from '../germplasm-details/germplasm-details-popup.service';
import { StudyEditorService } from '../study-editor/study-editor.service';
import { StudyEditorContext } from '../study-editor/study-editor.context';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { ParentModel } from '../shared/study/model/parent.model';
import { GermplasmDetailsUrlService } from '../shared/germplasm/service/germplasm-details.url.service';
import { Program } from '../shared/program/model/program';
import { TranslateService } from '@ngx-translate/core';
import { MAX_NAME_DISPLAY_SIZE } from '../app.constants';

@Component({
    selector: 'jhi-crossing-settings',
    templateUrl: './crossing-settings-modal.component.html',
})
export class CrossingSettingsModalComponent implements OnInit, OnDestroy {
    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    defaultSeparator = '/';
    BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum;
    isBreedingMethodScreen = true;
    isPreview = false;
    useParentalStatus = true;
    applyGroupingToNewCrossesOnly = false;
    breedingMethodSelectedId: number;
    showApplyGroupingCheckBox = false;
    isAutomaticNaming = true;
    checkExistingCrosses = false;
    estimatedHarvestDate: NgbDate;
    locationId: number;
    presetSettingId = 0;
    presetSettings: CrossNamePresetSetting[];
    numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    originalList: PreviewCrossesModel[] = [];
    completePreviewList: PreviewCrossesModel[];
    listPerPage: PreviewCrossesModel[][];
    currentPagePreviewList: PreviewCrossesModel[];
    totalItems = 0;
    currentPageCount: number;
    page = 1;
    itemsPerPage = 10;
    isLoading = false;
    previousPage: number;

    crossNameSetting: CrossNameSetting;

    omitAlertedCrosses = false;
    showRecordsWithAlerts = false;
    hybridMethods = [];
    nextNameInSequence: string;
    sampleParentageDesignation: string;

    breedingMethodHelpLink: string;
    harvestLocationHelpLink: string;

    subscriptionManager = new SubscriptionsManager();

    constructor(private route: ActivatedRoute,
                private context: DesignCrossesContext,
                private eventManager: JhiEventManager,
                private crossService: CrossService,
                private languageService: JhiLanguageService,
                private alertService: AlertService,
                public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private paramContext: ParamContext,
                private helpService: HelpService,
                private dateHelperService: DateHelperService,
                private germplasmService: GermplasmService,
                private urlService: UrlService,
                private studyEditorContext: StudyEditorContext,
                private studyEditorService: StudyEditorService,
                private translateService: TranslateService,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService,
                private germplasmDetailsUrlService: GermplasmDetailsUrlService) {
        this.paramContext.readParams();
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit() {
        const hybridMethodsPromise = this.crossService.getHybridMethods().toPromise();
        hybridMethodsPromise.then((hybridMethods) => {
            this.hybridMethods = hybridMethods;
        });
        this.crossNameSetting = new CrossNameSetting(null, null, false, false, null, this.defaultSeparator, false, null);
        this.updateSampleParentageDesignation();
        this.loadPresets();

        if (this.context.crosses.some((cross) => cross.crossType === CrossType.MULTIPLE_PARENT)) {
            this.useParentalStatus = false;
        }

        if (!this.breedingMethodHelpLink || !this.breedingMethodHelpLink.length) {
            this.helpService.getHelpLink('MANAGE_CROP_BREEDING_METHODS').toPromise().then((response) => {
                this.breedingMethodHelpLink = response.body;
            });
        }

        if (!this.harvestLocationHelpLink || !this.harvestLocationHelpLink.length) {
            this.helpService.getHelpLink('MANAGE_CROP_BREEDING_LOCATIONS').toPromise().then((response) => {
                this.harvestLocationHelpLink = response.body;
            });
        }
    }

    close() {
        this.activeModal.dismiss('cancel');
    }

    goToNamingScreen() {
        this.isLoading = true;
        if (this.useParentalStatus) {
            this.proceedToNaming();
        } else {
            const crossDesign = this.context.crosses.find((cross) => cross.crossType === CrossType.MULTIPLE_PARENT);
            this.subscriptionManager.add = this.crossService.validateBreedingMethod({
                breedingMethodId: this.breedingMethodSelectedId,
                crossType: crossDesign ? crossDesign.crossType : this.context.crossType,
                femaleProgenitorsNumber: crossDesign ? crossDesign.femaleParents.length : this.context.crosses[0].femaleParents.length,
                maleProgenitorsNumber: crossDesign ? crossDesign.maleParents.length : this.context.crosses[0].maleParents.length
            }).subscribe((res: void) => {
                this.proceedToNaming();
            },() => this.isLoading = false);
        }

    }

    proceedToNaming() {
        this.isLoading = false;
        this.isBreedingMethodScreen = false;
        this.isPreview = false;
        this.completePreviewList = [];
        this.originalList = [];
    }

    goBackToBreedingMethodScreen() {
        this.isBreedingMethodScreen = true;
    }

    goToCrossesPreview() {
        this.isLoading = true;
        this.omitAlertedCrosses = false;
        this.showRecordsWithAlerts = false;
        const harvestDate = this.dateHelperService.convertFormattedNgbDateToString(this.estimatedHarvestDate, '-');
        const request =
            new PreviewCrossesRequestModel(this.context.crosses, this.breedingMethodSelectedId,
                this.useParentalStatus, this.checkExistingCrosses, harvestDate, this.context.crossType);
        this.subscriptionManager.add = this.crossService.getPreviewCross(this.context.studyId, request)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe(
                (res: PreviewCrossesModel[]) => {
                    this.completePreviewList = res.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
                    this.originalList.push(...this.completePreviewList);
                    this.processPagination();
                    this.loadPage(1, true);
                    this.isPreview = true;
                    this.isLoading = false;
                }, () => this.isLoading = false);
    }

    saveCrosses() {
        this.isLoading = true;
        const crosses = this.omitAlertedCrosses ? this.completePreviewList : this.originalList;
        const harvestDate = this.dateHelperService.convertFormattedNgbDateToString(this.estimatedHarvestDate, '-');
        const request = new SaveCrossesRequestModel(crosses, harvestDate, this.locationId, !this.applyGroupingToNewCrossesOnly,
            this.isAutomaticNaming ? NamingSource.AUTOMATIC : NamingSource.MANUAL, this.crossNameSetting, false);
        this.subscriptionManager.add = this.crossService.saveCrosses(this.context.studyId, request)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe(
                (res: PreviewCrossesModel[]) => this.onSaveCrossesSuccess(res),
                () => this.isLoading = false);
    }

    onSaveCrossesSuccess(savedCrosses: PreviewCrossesModel[]) {
        this.isLoading = false;
        this.alertService.success('design-crosses.save.success');
        if (this.activeModal) {
            this.activeModal.close();
        }

        const germplasmListCreationModalRef = this.modalService.open(GermplasmListCreationComponent as Component,
            { modalDialogClass: 'modal-lg', backdrop: 'static' });
        germplasmListCreationModalRef.componentInstance.model.listType = 'F1CRT';
        germplasmListCreationModalRef.componentInstance.entries = savedCrosses.map((cross) => {
            const entry: GermplasmListEntry = new GermplasmListEntry();
            entry.entryNo = cross.entryNumber;
            entry.gid = cross.gid;
            entry.groupName = cross.cross;
            entry.seedSource = cross.seedSource;
            return entry;
        });
        germplasmListCreationModalRef.result.then(() => {
            setTimeout(() => {
                if (this.context.openedDirectlyFromNewStudyEditor) {
                    this.openStudy();
                }
            }, 500);
        }, () => {
            // If activeModal exists, it means this modal is opened from within jhipster app. Not external iframe.
            if (this.context.openedDirectlyFromNewStudyEditor) {
                this.openStudy();
            }
        });
    }

    openStudy() {
        const program = new Program();
        program.crop = this.paramContext.cropName;
        program.id = String(this.paramContext.selectedProjectId);
        program.uniqueID = this.paramContext.programUUID;
        this.urlService.openStudy(this.context.studyId, this.context.studyName, program);
    }

    getMaleParentsDesignation(crossesModel: PreviewCrossesModel) {
        if (crossesModel.maleParents.length === 1) {
            return crossesModel.maleParents[0].designation;
        } else {
            let desig = '[';
            crossesModel.maleParents.forEach((maleParent, index) => {
                desig = desig + maleParent.designation;
                if (index === crossesModel.maleParents.length - 1) {
                    desig = desig + ']';
                } else {
                    desig = desig + ', ';
                }
            });
            return desig;
        }
    }

    viewExistingCrosses(previewCross: PreviewCrossesModel) {
        this.context.cross = previewCross;
        this.modalService.open(ExistingCrossesModalComponent as Component, { size: 'lg', backdrop: 'static' })
            .result.then(() => {
            });
    }

    getNextNameSequence() {
        if (this.crossNameSetting.prefix) {
            const germplasmNameSetting = new GermplasmNameSettingModel(this.crossNameSetting.prefix, this.crossNameSetting.suffix,
                this.crossNameSetting.addSpaceBetweenPrefixAndCode, this.crossNameSetting.addSpaceBetweenSuffixAndCode,
                this.crossNameSetting.numOfDigits, this.crossNameSetting.startNumber);
            this.germplasmService.getNextNameInSequence(germplasmNameSetting).toPromise().then((response) => {
                if (response.body) {
                    this.nextNameInSequence = response.body;
                }
            });
        }
    }

    updateSampleParentageDesignation() {
        this.sampleParentageDesignation = 'FEMALE' + this.crossNameSetting.separator + 'MALE';
    }

    loadPage(page: number, forceReload = false) {
        if (page !== this.previousPage || forceReload) {
            this.previousPage = page;
            this.currentPagePreviewList = this.listPerPage[page - 1];
            const itemCount = this.currentPagePreviewList.length;
            this.currentPageCount = ((page - 1) * this.itemsPerPage) + itemCount;
        }
    }

    resetTable(): void {
        this.page = 1;
        this.previousPage = 1;
        this.listPerPage = [];
        this.processPagination();
        this.loadPage(1, true);
    }

    processPagination() {
        this.totalItems = this.completePreviewList.length;

        if (this.totalItems === 0) {
            this.listPerPage = [];
            this.listPerPage.push([]);
            return;
        }

        this.listPerPage = this.completePreviewList.reduce((resultArray, item, index) => {
            const pageIndex = Math.floor(index / this.itemsPerPage);

            if (!resultArray[pageIndex]) {
                resultArray[pageIndex] = []; // start a new page
            }

            resultArray[pageIndex].push(item);

            return resultArray;
        }, []);
    }

    showOnlyRecordsWithAlerts() {
        if (this.showRecordsWithAlerts) {
            this.completePreviewList = this.originalList.filter((previewCross) => previewCross.hasExistingCrosses);
        } else {
            this.completePreviewList = this.originalList;
        }
        this.processPagination();
        this.loadPage(1, true);
    }

    omitRecordsWithAlerts() {
        if (this.omitAlertedCrosses) {
            this.showRecordsWithAlerts = false;
            this.completePreviewList = this.originalList.filter((previewCross) => !previewCross.hasExistingCrosses);
        } else {
            this.completePreviewList = this.originalList;
        }
        this.processPagination();
        this.loadPage(1, true);
    }

    disableGoToCrossesPreview() {
        return !this.estimatedHarvestDate || !this.locationId || (!this.isAutomaticNaming && (!this.crossNameSetting.prefix || this.crossNameSetting.prefix.length === 0));
    }

    saveCrossingNamePreset() {
        if (Number(this.presetSettingId) !== 0 ) {
            this.updatePreset();
        } else {
            const crossingNamePreset: CrossNamePresetSetting = new CrossNamePresetSetting();
            crossingNamePreset.toolId = 23;
            crossingNamePreset.toolSection = 'CROSSING_NAME_PRESET';
            crossingNamePreset.type = 'CrossNameSettingPreset';
            crossingNamePreset.programUUID = this.paramContext.programUUID;
            this.setPresetSettingValues(crossingNamePreset);
            const saveTemplateModalRef = this.modalService.open(SaveTemplateComponent as Component);
            saveTemplateModalRef.result.then((presetName) => {
                crossingNamePreset.name = presetName;
                this.subscriptionManager.add = this.crossService.addPreset(crossingNamePreset).subscribe((savedPreset ) => {
                    this.alertService.success('design-crosses.preset.save.success');
                    this.presetSettings.push(savedPreset);
                    this.presetSettingId = savedPreset.id;
                }, () => this.isLoading = false);
            });
        }
    }

    updatePreset() {
        const crossNamePresetSetting = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
        this.setPresetSettingValues(crossNamePresetSetting);
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.translateService.instant('design-crosses.preset.update.confirm.title');
        confirmModalRef.componentInstance.message = this.translateService.instant('design-crosses.preset.update.confirm.message', { param: crossNamePresetSetting.name });
        confirmModalRef.result.then(() => {
            this.subscriptionManager.add = this.crossService.updatePreset(crossNamePresetSetting).subscribe((res: void) => {
                this.alertService.success('design-crosses.preset.update.success');
            }, () => this.isLoading = false);
        });
    }

    setPresetSettingValues(crossingNamePreset) {
        crossingNamePreset.prefix = this.crossNameSetting.prefix;
        crossingNamePreset.suffix = this.crossNameSetting.suffix;
        crossingNamePreset.addSpaceBetweenSuffixAndCode = this.crossNameSetting.addSpaceBetweenSuffixAndCode;
        crossingNamePreset.addSpaceBetweenPrefixAndCode = this.crossNameSetting.addSpaceBetweenPrefixAndCode;
        crossingNamePreset.numOfDigits = this.crossNameSetting.numOfDigits;
        crossingNamePreset.separator = this.crossNameSetting.separator;
        crossingNamePreset.saveParentageDesignationAsAString = this.crossNameSetting.saveParentageDesignationAsAString;
        crossingNamePreset.startNumber = this.crossNameSetting.startNumber;
    }

    applySelectedSetting() {
        if (Number(this.presetSettingId) !== 0) {
            const crossingNamePreset = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
            this.crossNameSetting.prefix = crossingNamePreset.prefix;
            this.crossNameSetting.suffix = crossingNamePreset.suffix;
            this.crossNameSetting.addSpaceBetweenSuffixAndCode = crossingNamePreset.addSpaceBetweenSuffixAndCode;
            this.crossNameSetting.addSpaceBetweenPrefixAndCode = crossingNamePreset.addSpaceBetweenPrefixAndCode;
            this.crossNameSetting.numOfDigits = crossingNamePreset.numOfDigits;
            this.crossNameSetting.separator = crossingNamePreset.separator;
            this.crossNameSetting.saveParentageDesignationAsAString = crossingNamePreset.saveParentageDesignationAsAString;
            this.crossNameSetting.startNumber = crossingNamePreset.startNumber;
        }
    }

    deleteSelectedSetting() {
        const crossNamePresetSetting = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.translateService.instant('design-crosses.preset.delete.confirm.title');
        confirmModalRef.componentInstance.message = this.translateService.instant('design-crosses.preset.delete.confirm.message', { param: crossNamePresetSetting.name });
        confirmModalRef.result.then(() => {
            this.subscriptionManager.add = this.crossService.deletePreset(this.presetSettingId).subscribe(() => {
                this.alertService.success('design-crosses.preset.delete.success');
                this.loadPresets();
            }, () => this.isLoading = false);
        });
    }

    updateShowApplyGroupingCheckBoxValue() {
        this.showApplyGroupingCheckBox = !this.useParentalStatus && this.breedingMethodSelectedId && this.hybridMethods.includes(Number(this.breedingMethodSelectedId));
    }

    getNames(list: ParentModel[]) {
        if (list) {
            return list.map((parentModel) => parentModel.designation);
        }
        return [];
    }

    private loadPresets() {
        this.isLoading = true;
        this.subscriptionManager.add = this.crossService.getAllPresets('CROSSING_NAME_PRESET').subscribe((presets) => {
            this.presetSettings = presets;
            this.presetSettingId = 0;
            this.isLoading = false;
        }, () => this.isLoading = false);
    }

    openGermplasmDetailsInTab(gid: number) {
        const url = this.germplasmDetailsUrlService.getUrlAsString(gid);
        window.open(url, '_blank');
    }

}
